import React, { ErrorInfo, ReactNode } from 'react';

interface ChartErrorHandlerProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ChartErrorHandlerState {
  hasError: boolean;
  error: Error | null;
}

/**
 * A specialized error boundary specifically designed to catch and handle 
 * chart-related errors such as null property accesses or symbol lookups.
 * 
 * This component adds an extra layer of protection for chart rendering,
 * which often involves complex data transformations and lookups.
 */
export class ChartErrorHandler extends React.Component<ChartErrorHandlerProps, ChartErrorHandlerState> {
  constructor(props: ChartErrorHandlerProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    console.warn('ChartErrorHandler caught error:', error.message);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to help with debugging
    console.error('ChartErrorHandler:', error, errorInfo);
    
    // Intercept specific null/undefined symbol errors to provide more helpful diagnostics
    if (error.message.includes('is null') || error.message.includes('is undefined')) {
      console.warn(`It looks like there was a problem with symbol data lookup. 
        This is likely due to malformed data or a race condition where data is
        accessed before it's fully loaded.`);
    }
  }

  render() {
    if (this.state.hasError) {
      // Return user-friendly fallback UI
      return this.props.fallback || (
        <div className="bg-[#1E1E2E] border border-[#383854] rounded-lg p-4 text-center">
          <div className="text-sm text-[#14F195] mb-2">Chart data visualization issue</div>
          <div className="text-xs text-gray-400">Please check back soon for updated data.</div>
        </div>
      );
    }

    // If no error occurred, render children normally
    return this.props.children;
  }
}

/**
 * A utility HOC to wrap any chart component with error handling
 */
export function withChartErrorHandling<P extends object>(
  Component: React.ComponentType<P>
): React.FC<P & { fallback?: ReactNode }> {
  return function WrappedComponent(props: P & { fallback?: ReactNode }) {
    const { fallback, ...componentProps } = props;
    return (
      <ChartErrorHandler fallback={fallback}>
        <Component {...componentProps as P} />
      </ChartErrorHandler>
    );
  };
}

export default ChartErrorHandler; 